import { storyBeats, type StoryBeat } from '@/content/my-story';

/** Per-beat accent colors for the chapter marker bar. */
const beatAccents = [
  'bg-primary', // teal
  'bg-urgency', // coral
  'bg-violet-500', // violet
  'bg-cta', // warm orange
] as const;

function Beat({ beat, index }: { beat: StoryBeat; index: number }) {
  const accent = beatAccents[index % beatAccents.length];

  // Place the pull quote after the first paragraph for rhythm: hook → quote → detail.
  const firstPara = beat.paragraphs[0];
  const restParas = beat.paragraphs.slice(1);

  return (
    <li
      id={beat.id}
      className="scroll-mt-24 border-t border-border/50 py-14 first:border-t-0 first:pt-6 sm:py-16"
    >
      {/* Chapter marker */}
      <div className="mb-6 flex items-center gap-4">
        <span
          aria-hidden
          className={`h-9 w-1.5 rounded-full ${accent}`}
        />
        <span
          aria-hidden
          className="font-display text-3xl font-bold tracking-tight tabular-nums text-foreground/15 sm:text-4xl"
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <h2 className="font-display text-foreground text-2xl font-semibold tracking-tight sm:text-3xl">
          {beat.title}
        </h2>
      </div>

      {/* Opening paragraph */}
      <p className="text-foreground/90 text-base leading-[1.8] sm:text-lg sm:leading-[1.85]">
        {firstPara}
      </p>

      {/* Pull quote as a mid-section breath */}
      {beat.pullQuote ? (
        <blockquote className="my-8 border-l-2 border-primary/30 pl-5 sm:my-10 sm:pl-7">
          <p className="font-display text-foreground text-xl leading-snug font-medium text-balance sm:text-2xl md:leading-snug">
            &ldquo;{beat.pullQuote}&rdquo;
          </p>
        </blockquote>
      ) : null}

      {/* Remaining paragraphs */}
      <div className="space-y-5">
        {restParas.map((paragraph, pIndex) => (
          <p
            key={`${beat.id}-${pIndex}`}
            className="text-foreground/90 text-base leading-[1.8] sm:text-lg sm:leading-[1.85]"
          >
            {paragraph}
          </p>
        ))}
      </div>
    </li>
  );
}

export function StoryBeats() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-6 sm:px-6 lg:px-8">
      <ol className="flex list-none flex-col p-0">
        {storyBeats.map((beat, index) => (
          <Beat key={beat.id} beat={beat} index={index} />
        ))}
      </ol>
    </div>
  );
}
